import { type GroupPages, pagedRows } from "@common/api/utils"
import type { GroupCountPublic } from "@common/client"
import {
  hashKey,
  type QueryClient,
  useQueries,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { useEffect, useSyncExternalStore } from "react"

type Grouped = {
  data: unknown[]
  count: number
  groups: GroupCountPublic[]
}

export type PagedGroup<R, G extends GroupCountPublic = GroupCountPublic> = G & {
  rows: R[]
  isLoading: boolean
  isError: boolean
  remaining: number
}

export type GroupPaging<R, G extends GroupCountPublic = GroupCountPublic> = {
  groups: PagedGroup<R, G>[]
  rows: R[]
  reveal: (groupKey: string) => void
  showMore: (groupKey: string) => void
  retry: (groupKey: string) => void
}

export type GroupOrder<R> = {
  indexOf: (rowKey: string) => number
  previous: (rowKey: string) => R | undefined
  next: (rowKey: string) => Promise<R | undefined>
}

type Shown = ReadonlyMap<string, number>

const NOTHING_SHOWN: Shown = new Map()

const shownByScope = new Map<string, Shown>()

const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const watchedClients = new WeakSet<QueryClient>()

function forgetEvictedScopes(queryClient: QueryClient): void {
  if (watchedClients.has(queryClient)) return
  watchedClients.add(queryClient)
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type === "removed") shownByScope.delete(event.query.queryHash)
  })
}

function firstPages<R>(
  rows: R[],
  groups: GroupCountPublic[],
): Map<string, R[]> {
  const pages = new Map<string, R[]>()
  let start = 0
  for (const group of groups) {
    pages.set(group.key, rows.slice(start, start + group.included))
    start += group.included
  }
  return pages
}

function show(
  scope: string,
  groupKey: string,
  inline: number,
  next: (current: number) => number,
): void {
  const current = shownByScope.get(scope) ?? NOTHING_SHOWN
  const count = next(current.get(groupKey) ?? inline)
  if (current.get(groupKey) === count) return
  shownByScope.set(scope, new Map(current).set(groupKey, count))
  for (const listener of listeners) listener()
}

export function useGroupPages<I, O extends Grouped, E>(
  pages: GroupPages<I, O, E>,
  input: I,
  keyOf: (row: O["data"][number]) => string,
): {
  data: O
  paging: GroupPaging<O["data"][number], O["groups"][number]>
  order: GroupOrder<O["data"][number]>
} {
  const queryClient = useQueryClient()
  const groupsQuery = pages.groups(input)
  const { data } = useSuspenseQuery(groupsQuery)
  const scope = hashKey(groupsQuery.queryKey)
  const counts = useSyncExternalStore(
    subscribe,
    () => shownByScope.get(scope) ?? NOTHING_SHOWN,
  )

  useEffect(() => forgetEvictedScopes(queryClient), [queryClient])

  const firsts = firstPages(data.data, data.groups)
  const inlineOf = (groupKey: string) =>
    (firsts.get(groupKey)?.length ?? 0) > 0 ? 1 : 0
  const requests = data.groups.flatMap((group) => {
    const inline = inlineOf(group.key)
    const shown = counts.get(group.key) ?? inline
    return Array.from({ length: shown - inline }, (_, at) => ({
      groupKey: group.key,
      index: inline + at,
    }))
  })
  const results = useQueries({
    queries: requests.map(({ groupKey, index }) =>
      pages.page(input, groupKey, index),
    ),
  })
  const resultsOf = (groupKey: string) =>
    results.filter((_, at) => requests[at]?.groupKey === groupKey)

  const groups = data.groups.map((group) => {
    const loaded = resultsOf(group.key)
    const total = loaded[0]?.data?.count ?? group.count
    const fetched = loaded.flatMap((page) => (page.data ? [page.data] : []))
    const first = firsts.get(group.key) ?? []
    return {
      ...group,
      rows: pagedRows([{ data: first }, ...fetched], keyOf),
      isLoading:
        (first.length === 0 && loaded.length === 0) ||
        loaded.some((result) => result.isPending),
      isError: loaded.some((result) => result.isError),
      remaining: Math.max(
        total - first.length - loaded.length * pages.pageSize,
        0,
      ),
    }
  })

  const rows = groups.flatMap((group) => group.rows)

  const fetchPage = async (groupKey: string, index: number) => {
    const page = await queryClient.fetchQuery(
      pages.page(input, groupKey, index),
    )
    show(scope, groupKey, inlineOf(groupKey), (current) =>
      Math.max(current, index + 1),
    )
    return page.data
  }

  const locate = (rowKey: string) => {
    const at = groups.findIndex((group) =>
      group.rows.some((row) => keyOf(row) === rowKey),
    )
    const group = groups[at]
    return group
      ? {
          at,
          group,
          position: group.rows.findIndex((row) => keyOf(row) === rowKey),
        }
      : undefined
  }

  return {
    data,
    order: {
      indexOf: (rowKey) => {
        const found = locate(rowKey)
        if (!found) return -1
        const before = groups.slice(0, found.at)
        return before.reduce((sum, group) => sum + group.count, found.position)
      },
      previous: (rowKey) => {
        const index = rows.findIndex((row) => keyOf(row) === rowKey)
        return index > 0 ? rows[index - 1] : undefined
      },
      next: async (rowKey) => {
        const found = locate(rowKey)
        if (!found) return undefined
        const { at, group, position } = found
        const loaded = group.rows[position + 1]
        if (loaded) return loaded
        if (group.rows.length < group.count) {
          const index = Math.floor(group.rows.length / pages.pageSize)
          const seen = new Set(group.rows.map(keyOf))
          const page = await fetchPage(group.key, index)
          return page.find((row) => !seen.has(keyOf(row)))
        }
        const following = groups[at + 1]
        if (!following) return undefined
        return following.rows[0] ?? (await fetchPage(following.key, 0))[0]
      },
    },
    paging: {
      groups,
      rows,
      reveal: (groupKey) =>
        show(scope, groupKey, inlineOf(groupKey), (current) =>
          Math.max(current, 1),
        ),
      showMore: (groupKey) =>
        show(scope, groupKey, inlineOf(groupKey), (current) => current + 1),
      retry: (groupKey) => {
        for (const result of resultsOf(groupKey)) {
          if (result.isError) void result.refetch()
        }
      },
    },
  }
}
